import { refreshSession } from "@/services/tokenRefresh";
import { ENV } from "@/config/env";
import { companyRequest } from "@/services/companyRequest";
import sessionManager from "@/services/sessionManager";
import { createUuid } from "@/utils/uuid";
import { collectConversationPages } from "./conversationLibrary.mjs";
import { readErrorDetail } from "./error-response.mjs";
import { createResubmitPayload } from "./retryRequest.mjs";
import {
  createStreamCompletionTracker,
  dispatchStreamEvent,
  parseSseBlock,
} from "./streaming.mjs";

const aiBasePath = (companyId, facilityId) =>
  `/api/v2/facilities/${encodeURIComponent(companyId)}/${encodeURIComponent(facilityId)}/ai`;

const REASONING_EFFORTS = new Set(["none", "low", "medium", "high", "xhigh", "max"]);

const unwrap = (response) => response?.data ?? response;

const createResponseError = async (response, fallbackMessage) => {
  const detail = await readErrorDetail(response);
  const error = new Error(detail || fallbackMessage);
  error.status = response.status;
  return error;
};

const refreshAccessToken = async (_refresh, signal) => (await refreshSession({ signal })).access;

export const listFacilityAiConversations = (companyId, facilityId) =>
  collectConversationPages(async (page) => {
    const response = await companyRequest.get(`${aiBasePath(companyId, facilityId)}/conversations/`, {
      params: { page, page_size: 100 },
    });
    return unwrap(response);
  });

export const createFacilityAiConversation = async (companyId, facilityId) =>
  unwrap(await companyRequest.post(`${aiBasePath(companyId, facilityId)}/conversations/`, {}));

export const getFacilityAiConversation = async (companyId, facilityId, conversationId) => {
  const response = await companyRequest.get(
    `${aiBasePath(companyId, facilityId)}/conversations/${conversationId}/`
  );
  return unwrap(response);
};

export const getFacilityAiConversationMessages = async (companyId, facilityId, conversationId) => {
  const response = await companyRequest.get(
    `${aiBasePath(companyId, facilityId)}/conversations/${conversationId}/messages/`
  );
  return unwrap(response);
};

export const submitFacilityAiFeedback = async ({
  companyId,
  facilityId,
  messageId,
  payload,
}) => {
  const response = await companyRequest.put(
    `${aiBasePath(companyId, facilityId)}/messages/${messageId}/feedback/`,
    payload
  );
  return unwrap(response);
};

export const listFacilityAiFeedbackForReview = async ({
  status,
  rating,
  page = 1,
  pageSize = 50,
} = {}) => {
  const params = { page, page_size: pageSize };
  if (status) params.status = status;
  if (rating) params.rating = rating;
  const response = await companyRequest.get("/api/v2/ai/feedback/review/", { params });
  return unwrap(response);
};

export const reviewFacilityAiFeedback = async (feedbackId, payload) => {
  const response = await companyRequest.patch(
    `/api/v2/ai/feedback/review/${feedbackId}/`,
    payload
  );
  return unwrap(response);
};

export const updateFacilityAiConversation = async (companyId, facilityId, conversationId, payload) => {
  const response = await companyRequest.patch(
    `${aiBasePath(companyId, facilityId)}/conversations/${conversationId}/`,
    payload
  );
  return unwrap(response);
};

export const deleteFacilityAiConversation = async (companyId, facilityId, conversationId) => {
  await companyRequest.delete(`${aiBasePath(companyId, facilityId)}/conversations/${conversationId}/`);
};

export const transcribeFacilityAiAudio = async ({
  companyId,
  facilityId,
  blob,
  language = "",
  signal,
}) => {
  const formData = new FormData();
  formData.append("audio", blob, "voice.wav");
  if (language) {
    formData.append("language", language);
  }
  const response = await companyRequest.post(
    `${aiBasePath(companyId, facilityId)}/transcribe/`,
    formData,
    // companyRequest defaults to application/json, which makes axios serialize
    // FormData into JSON. Override so the browser sends multipart + boundary.
    { signal, headers: { "Content-Type": "multipart/form-data" } }
  );
  return unwrap(response);
};

export const prepareFacilityWorkorderDraft = async ({ companyId, facilityId, text, signal }) => {
  const response = await companyRequest.post(
    `${aiBasePath(companyId, facilityId)}/workorders/draft/`,
    { text },
    { signal, timeout: 65000 }
  );
  const payload = unwrap(response);
  if (!payload || payload.version !== 1 || payload.saved !== false) {
    throw new Error("Could not prepare the draft. Your text has been kept; please try again.");
  }
  return payload;
};

export const uploadFacilityAiAttachment = async ({
  companyId,
  facilityId,
  conversationId,
  file,
  signal,
}) => {
  const formData = new FormData();
  formData.append("file", file, file.name || "attachment");
  if (conversationId) {
    formData.append("conversation_id", String(conversationId));
  }
  const response = await companyRequest.post(
    `${aiBasePath(companyId, facilityId)}/attachments/`,
    formData,
    // companyRequest defaults to application/json, which makes axios serialize
    // FormData into JSON. Override so the browser sends multipart + boundary.
    { signal, headers: { "Content-Type": "multipart/form-data" } }
  );
  return unwrap(response);
};

export const streamFacilityAiChat = async ({
  companyId,
  facilityId,
  conversationId,
  message,
  webSearch = "off",
  reasoningEffort = "medium",
  pageContext,
  interactionReply,
  attachmentIds = [],
  resubmit = false,
  resubmitMessageId,
  signal,
  onEvent,
}) => {
  const session = sessionManager.getSessionSnapshot();
  const traceId = createUuid();
  const payload = {
    message,
    company_id: Number(companyId),
    facility_id: Number(facilityId),
    web_search: webSearch === "auto" ? "auto" : "off",
    llm_reasoning_effort: REASONING_EFFORTS.has(reasoningEffort)
      ? reasoningEffort
      : "medium",
  };

  if (conversationId) {
    payload.conversation_id = conversationId;
  }

  if (pageContext) {
    payload.page_context = pageContext;
  }
  if (interactionReply) payload.interaction_reply = interactionReply;
  if (attachmentIds.length) payload.attachment_ids = attachmentIds;

  if (resubmit) {
    Object.assign(payload, createResubmitPayload(conversationId, resubmitMessageId));
  }

  const streamUrl = `${ENV.backendUrl}${aiBasePath(companyId, facilityId)}/chat/stream/`;
  const requestStream = (access) => fetch(streamUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: access ? `Bearer ${access}` : "",
      "X-Trace-Id": traceId,
    },
    body: JSON.stringify(payload),
    signal,
  });

  let response = await requestStream(session.access);
  if (response.status === 401) {
    const refresh = sessionManager.getSessionSnapshot().refresh;
    if (!refresh || signal?.aborted) {
      await sessionManager.reset({ reason: "unauthorized" });
      throw await createResponseError(response, "AI request failed with status 401");
    }

    try {
      const access = await refreshAccessToken(refresh, signal);
      response = await requestStream(access);
    } catch (refreshError) {
      throw refreshError;
    }
  }

  if (!response.ok || !response.body) {
    throw await createResponseError(
      response,
      `AI request failed with status ${response.status}`
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  const completion = createStreamCompletionTracker();

  const deliverEvent = async (event) => {
    await dispatchStreamEvent(event, onEvent);
    completion.observe(event);
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    // Keep the raw buffer until a complete LF/CRLF separator is present so a
    // separator split across ReadableStream chunks cannot drop an event.
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() || "";

    for (const block of blocks) {
      const event = parseSseBlock(block);
      await deliverEvent(event);
    }
  }

  buffer += decoder.decode();
  if (buffer.trim()) {
    const event = parseSseBlock(buffer);
    await deliverEvent(event);
  }
  completion.assertCompleted();
};

const interactionRequest = async ({ companyId, facilityId, path, method = "GET", body, signal }) => {
  const send = (access) => fetch(`${ENV.backendUrl}${aiBasePath(companyId, facilityId)}${path}`, {
    method, signal,
    headers: { "Content-Type": "application/json", Authorization: access ? `Bearer ${access}` : "" },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let response = await send(sessionManager.getSessionSnapshot().access);
  if (response.status === 401) {
    const refresh = sessionManager.getSessionSnapshot().refresh;
    if (refresh && !signal?.aborted) response = await send(await refreshAccessToken(refresh, signal));
  }
  if (!response.ok) throw await createResponseError(response, `Task request failed (${response.status}).`);
  return response.json();
};

export const getInteractionTask = (args) => interactionRequest({ ...args, path: `/conversations/${args.conversationId}/task/` });
export const getInteractionCapabilities = (args) => interactionRequest({ ...args, path: "/interactions/" });
export const cancelInteractionTask = (args) => interactionRequest({ ...args, path: `/conversations/${args.conversationId}/task/`, method: "DELETE" });
export const confirmInteractionAction = (args) => interactionRequest({ ...args,
  path: `/actions/${args.actionId}/confirm/`, method: "POST",
  body: { revision: args.revision, task_revision: args.taskRevision },
});
export const reviseInteractionAction = (args) => interactionRequest({ ...args,
  path: `/actions/${args.actionId}/revise/`, method: "POST",
  body: { revision: args.revision, task_revision: args.taskRevision, changes: args.changes },
});
